import React, { useState } from 'react';
import { fn } from '../api/functions';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

export default function SmartAIQuestionGenerator({ 
  story, 
  onQuestionsUpdate, 
  onCancel 
}) {
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedForAdding, setSelectedForAdding] = useState([]);
  const [targetingPrompt, setTargetingPrompt] = useState('');
  const [storyContext, setStoryContext] = useState(null);

  // Gather story context when user clicks generate (not on mount)
  const gatherStoryContext = async () => {
    if (!story.id) {
      console.log('No story ID provided');
      return null;
    }
    
    try {
      console.log('Gathering story context for:', story.title);
      
      // Get existing questions (last 10 only for context and token optimization)
      const allExistingQuestions = story?.selectedQuestions || story?.questions || [];
      const existingQuestions = allExistingQuestions.slice(-10); // Only use last 10 questions
      
      // Get all answers with transcripts by querying through questions
      const answersWithTranscripts = [];
      const followupAnswers = [];
      let totalAnswers = 0;
      
      // Instead of direct access to answers collection, iterate through questions
      // This avoids Firebase permissions issues and follows proper data structure
      const questionsQuery = collection(db, 'stories', story.id, 'questions');
      const questionsSnapshot = await getDocs(questionsQuery);
      
      for (const questionDoc of questionsSnapshot.docs) {
        const answersQuery = collection(questionDoc.ref, 'answers');
        const answersSnapshot = await getDocs(answersQuery);
        totalAnswers += answersSnapshot.size;
        
        answersSnapshot.forEach((answerDoc) => {
          const data = answerDoc.data();
          if (data.transcript && data.transcript.trim() && data.transcript !== 'Audio recording (transcription failed)') {
            const questionText = typeof data.question === 'string' ? data.question : data.question?.text || null;
            
            // Skip orphaned answers with missing or invalid questions
            if (!questionText || questionText === 'Unknown question') {
              const displayNumber = data.displayOrder !== undefined ? data.displayOrder + 1 : 
                                    data.qNumber !== undefined ? data.qNumber + 1 : '?';
              console.log(`Skipping orphaned answer: Q${displayNumber} - no valid question text`);
              return;
            }
            
            const answerData = {
              question: questionText,
              transcript: data.transcript,
              qNumber: data.qNumber, // Keep for backward compatibility
              displayOrder: data.displayOrder, // New UUID-based system
              questionId: data.questionId // UUID reference
            };
            answersWithTranscripts.push(answerData);
            
            // Include follow-up answers if they exist
            if (data.followups && Array.isArray(data.followups)) {
              data.followups.forEach(followup => {
                if (followup.transcript && followup.transcript.trim()) {
                  followupAnswers.push({
                    question: typeof followup.question === 'string' ? followup.question : followup.question?.text || 'Follow-up question',
                    transcript: followup.transcript,
                    parentQuestion: answerData.question
                  });
                }
              });
            }
          }
        });
      }
      
      console.log(`🔍 Found ${totalAnswers} total answer documents in database`);
      
      // Build comprehensive story context for gap analysis
      const context = {
        title: story.title || 'Untitled Story',
        userName: story.userName || story.recipientName || 'Unknown Person',
        birthYear: story.birthYear || '',
        hometown: story.hometown || '',
        jobs: story.jobs || '',
        notes: story.notes || '',
        recipientName: story.recipientName || '',
        purchaserName: story.purchaserName || '',
        relationship: story.relationshipToSender || '',
        isGift: !!story.recipientName,
        
        // Focus on story progression and timeline gaps
        existingQuestions: existingQuestions.map(q => typeof q === 'string' ? q : q.text || q),
        
        // Story context for gap analysis - truncated for token efficiency
        answersWithTranscripts: answersWithTranscripts.map(answer => ({
          question: answer.question,
          transcript: answer.transcript.length > 200 ? answer.transcript.substring(0, 200) + '...' : answer.transcript,
          qNumber: answer.qNumber, // Keep for backward compatibility
          displayOrder: answer.displayOrder, // UUID-based system
          questionId: answer.questionId // UUID reference
        })),
        
        followupAnswers: followupAnswers.map(followup => ({
          question: followup.question,
          transcript: followup.transcript.length > 150 ? followup.transcript.substring(0, 150) + '...' : followup.transcript,
          parentQuestion: followup.parentQuestion
        })),
        
        // Stats for story analysis
        totalQuestions: allExistingQuestions.length,
        analyzedQuestions: existingQuestions.length,
        answeredQuestions: answersWithTranscripts.length,
        totalFollowups: followupAnswers.length
      };
      
      console.log(`📊 Context gathered: ${answersWithTranscripts.length} valid answers, ${followupAnswers.length} follow-ups, ${Math.min(allExistingQuestions.length, 10)}/${allExistingQuestions.length} questions analyzed`);
      console.log(`🧹 Filtered out orphaned answers (total database answers: ${totalAnswers})`);
      console.log('');
      
      // Log the actual questions for debugging
      console.log('=== STORY QUESTIONS (Frontend) ===');
      allExistingQuestions.forEach((q, i) => {
        console.log(`${i + 1}. ${q}`);
      });
      
      console.log('=== ANSWERED QUESTIONS (Database) ===');
      answersWithTranscripts.forEach((answer, i) => {
        const isOrphan = !allExistingQuestions.includes(answer.question);
        const displayNumber = answer.displayOrder !== undefined ? answer.displayOrder + 1 : 
                              answer.qNumber !== undefined ? answer.qNumber + 1 : '?';
        console.log(`${i + 1}. Q${displayNumber}: ${answer.question} ${isOrphan ? '⚠️ ORPHAN' : '✅'}`);
        console.log(`   Answer preview: ${answer.transcript.substring(0, 100)}...`);
      });
      
      console.log('=== FOLLOW-UP ANSWERS ===');
      followupAnswers.forEach((followup, i) => {
        console.log(`${i + 1}. ${followup.question} (related to: ${followup.parentQuestion})`);
        console.log(`   Answer preview: ${followup.transcript.substring(0, 100)}...`);
      });
      setStoryContext(context);
      return context;
      
    } catch (error) {
      console.error('Error gathering story context:', error);
      return null;
    }
  };

  const handleGenerateQuestions = async () => {
    setIsGenerating(true);
    try {
      // Gather context on-demand instead of on mount
      let context = storyContext;
      if (!context) {
        context = await gatherStoryContext();
        if (!context) {
          alert('Failed to load story context. Please try again.');
          return;
        }
      }

      console.log('Generating smart questions with context:', context);

      const result = await fn.generateSmartFollowupQuestions({
        storyContext: context,
        targetingPrompt: targetingPrompt.trim() || null
      });
      
      setGeneratedQuestions(result.data.questions || []);
      setSelectedForAdding([]); // Reset selection
    } catch (error) {
      console.error('Error generating smart questions:', error);
      alert('Failed to generate questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleSelection = (index) => {
    setSelectedForAdding(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleAddSelectedQuestions = () => {
    if (selectedForAdding.length === 0) {
      alert('Please select at least one question to add.');
      return;
    }

    const questionsToAdd = selectedForAdding.map(index => generatedQuestions[index]);
    const currentQuestions = story?.selectedQuestions || story?.questions || [];
    const updatedQuestions = [
      ...currentQuestions.map(q => typeof q === 'string' ? q : q.text || q),
      ...questionsToAdd
    ];

    onQuestionsUpdate(updatedQuestions);
    
    // Remove selected questions from the generated list
    const remainingQuestions = generatedQuestions.filter((_, index) => !selectedForAdding.includes(index));
    setGeneratedQuestions(remainingQuestions);
    setSelectedForAdding([]);
  };

  return (
    <div style={{ 
      padding: '0.5rem', 
      maxWidth: '100%', 
      position: 'relative',
      boxSizing: 'border-box',
      width: '100%'
    }}>
      
      {/* Close Button */}
      <button
        onClick={onCancel}
        style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          background: 'none',
          border: 'none',
          fontSize: '1.5rem',
          color: '#666',
          cursor: 'pointer',
          padding: '0.25rem',
          borderRadius: '4px',
          lineHeight: 1,
          zIndex: 10
        }}
        title="Close wizard"
      >
        ×
      </button>
      
      {/* Targeting Prompt Input */}
      <div className="memory-stream-card" style={{ 
        marginBottom: '1rem',
        marginRight: '0',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h4 style={{ margin: '0 0 0.75rem 0', color: '#061D3D', fontSize: '1rem' }}>Story Focus (Optional)</h4>
        <textarea
          value={targetingPrompt}
          onChange={(e) => setTargetingPrompt(e.target.value)}
          placeholder="E.g., 'Explore missing childhood years', 'Find gaps in their career timeline', 'What relationships haven't been discussed?'"
          rows={6}
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: '0',
            padding: '0.75rem',
            border: '1px solid var(--memory-stream-orange)',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#061D3D',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
        />
        <p style={{ 
          margin: '0.75rem 0 0 0', 
          fontSize: '0.8rem', 
          color: '#666',
          fontStyle: 'italic' 
        }}>
          💡 Leave blank for AI to analyze existing answers and generate smart follow-up questions automatically
        </p>
      </div>

      {/* Generate Button */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <button
          onClick={handleGenerateQuestions}
          disabled={isGenerating}
          className="memory-stream-button"
          style={{
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.6 : 1,
            transition: 'all 0.3s ease',
            width: '100%',
            maxWidth: '320px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {isGenerating && (
            <span style={{
              display: 'inline-block',
              width: '16px',
              height: '16px',
              border: '2px solid #fff',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
          )}
          {isGenerating ? 'Analyzing Timeline...' : '✨ Generate Smart Questions'}
        </button>
        
        {/* Add keyframe animation for spinner */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Generated Questions */}
      {generatedQuestions.length > 0 && (
        <div className="memory-stream-card" style={{ 
          marginBottom: '1rem',
          marginRight: '0',
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: 'none',
          border: '1px solid #E5E5E5' 
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#061D3D', fontSize: '1rem' }}>
            🎯 Generated Follow-up Questions ({generatedQuestions.length})
          </h4>
          
          <div style={{ marginBottom: '1rem' }}>
            {generatedQuestions.map((question, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '1rem',
                  marginBottom: '0.75rem',
                  backgroundColor: selectedForAdding.includes(index) ? '#FFF8E1' : '#F8F9FA',
                  border: selectedForAdding.includes(index) ? '2px solid var(--memory-stream-orange)' : '1px solid #E0E0E0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleToggleSelection(index)}
              >
                <input
                  type="checkbox"
                  checked={selectedForAdding.includes(index)}
                  onChange={() => handleToggleSelection(index)}
                  style={{ 
                    marginTop: '2px',
                    accentColor: 'var(--memory-stream-orange)',
                    minWidth: '16px',
                    flexShrink: 0
                  }}
                />
                <span style={{ 
                  color: '#061D3D', 
                  lineHeight: '1.5',
                  flex: 1,
                  fontSize: '0.95rem'
                }}>
                  {question}
                </span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '0.75rem',
            borderTop: '1px solid #E0E0E0',
            paddingTop: '1rem'
          }}>
            <button
              onClick={handleAddSelectedQuestions}
              disabled={selectedForAdding.length === 0}
              className="memory-stream-button"
              style={{
                cursor: selectedForAdding.length > 0 ? 'pointer' : 'not-allowed',
                opacity: selectedForAdding.length > 0 ? 1 : 0.5,
                width: '100%'
              }}
            >
              ➕ Add Selected ({selectedForAdding.length})
            </button>
            
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem' 
            }}>
              <button
                onClick={() => setSelectedForAdding(generatedQuestions.map((_, i) => i))}
                className="memory-stream-button-secondary"
                style={{ flex: 1 }}
              >
                ✓ Select All
              </button>
              
              <button
                onClick={() => setSelectedForAdding([])}
                style={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #666',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {generatedQuestions.length === 0 && !isGenerating && (
        <div style={{ 
          textAlign: 'center', 
          color: '#666', 
          fontStyle: 'italic',
          padding: '1.5rem 1rem',
          backgroundColor: '#F8F9FA',
          borderRadius: '8px',
          border: '1px solid #E0E0E0',
          fontSize: '0.9rem'
        }}>
          💡 Click "Generate Smart Questions" to analyze your story and create personalized follow-up questions
        </div>
      )}
    </div>
  );
}
